import React, { useState, useRef, useEffect } from "react";
import "./styles.css";

export interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  idPrefix?: string;
  className?: string;
  autoFocus?: boolean;
}

const CodeInput: React.FC<CodeInputProps> = ({
  length = 5,
  value,
  onChange,
  idPrefix = "code-input",
  className = "",
  autoFocus = false,
}) => {
  const [codeInputs, setCodeInputs] = useState<string[]>(Array.from({ length }, () => ""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Sync external value with internal state
  useEffect(() => {
    if (value.length <= length) {
      const newInputs = Array.from({ length }, (_, i) => value[i] || "");
      setCodeInputs(newInputs);
    }
  }, [value, length]);

  const handleCodeChange = (index: number, newValue: string) => {
    // Only allow single digit
    if (newValue.length > 1) return;

    const newCodeInputs = [...codeInputs];
    newCodeInputs[index] = newValue;
    setCodeInputs(newCodeInputs);

    const code = newCodeInputs.join("");
    onChange(code);

    // Auto-focus next input
    if (newValue && index < length - 1) {
      const nextInput = inputRefs.current[index + 1];
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !codeInputs[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);

    if (pastedData) {
      const newCodeInputs = Array.from({ length }, (_, i) => pastedData[i] || "");
      setCodeInputs(newCodeInputs);
      onChange(pastedData);

      // Focus the next empty input or the last input
      const nextEmptyIndex = Math.min(pastedData.length, length - 1);
      const nextInput = inputRefs.current[nextEmptyIndex];
      nextInput?.focus();
    }
  };

  return (
    <div className={`code-input-container ${className}`}>
      {codeInputs.map((inputValue, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          id={`${idPrefix}-${index}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="code-input"
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "");
            handleCodeChange(index, val);
          }}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          autoFocus={autoFocus && index === 0}
        />
      ))}
    </div>
  );
};

export default CodeInput;
